import type {
  ClientPresentationNode,
  PresentationSourceSpan,
  ReportSection,
  SectionPresentationMapping,
} from "./domain";

function hash(value:string,prefix:string){
  let result=2166136261;
  for(let index=0;index<value.length;index+=1){
    result^=value.charCodeAt(index);
    result=Math.imul(result,16777619);
  }
  return`${prefix}-${(result>>>0).toString(16).padStart(8,"0")}`;
}

export function exactPresentationSourceChecksum(value:string){
  return hash(value,"presentation-source");
}

export function substantivePresentationSourceFingerprint(value:string){
  const normalised=value.normalize("NFKC").replace(/\r\n?/g,"\n").replace(/[ \t]+/g," ").replace(/\n{3,}/g,"\n\n").trim();
  return hash(normalised,"presentation-substance");
}

export function presentationSourceValue(section:ReportSection,key:string):string|undefined{
  const one=section.sectionOneWorkflow?.clientDraft;
  const three=section.sectionThreeWorkflow?.clientDraft;
  const four=section.sectionFourWorkflow;
  const values:Record<string,string|undefined>={
    "sectionOne.clientDraft.introduction":one?.introduction,
    "sectionOne.clientDraft.currentUnderstanding":one?.currentUnderstanding,
    "sectionOne.clientDraft.inaccuracies":one?.inaccuracies,
    "sectionOne.clientDraft.sources":one?.sources,
    "sectionOne.clientDraft.nextStep":one?.nextStep,
    "sectionThree.clientDraft.websiteAssessment":three?.websiteAssessment,
    "sectionThree.clientDraft.recommendedWebsiteActions":three?.recommendedWebsiteActions,
    "sectionFour.promptTestingMethodology.content":four?.promptTestingMethodology?.content,
    "sectionFour.clientDraft.promptTestingExplanation":four?.clientDraft?.promptTestingExplanation,
  };
  return values[key];
}

export function validatePresentationMapping(mapping:SectionPresentationMapping,source:string){
  const checksumMatches=mapping.exactSourceChecksum===exactPresentationSourceChecksum(source);
  const fingerprintMatches=mapping.substantiveSourceFingerprint===substantivePresentationSourceFingerprint(source);
  let cursor=0;
  const ordered=mapping.spans.every(span=>{
    const valid=Number.isInteger(span.start)&&Number.isInteger(span.end)&&span.start===cursor&&span.end>span.start&&span.end<=source.length;
    cursor=span.end;
    return valid;
  });
  const complete=ordered&&cursor===source.length&&mapping.spans.map(span=>source.slice(span.start,span.end)).join("")===source;
  const content=mapping.spans.every(span=>(span.contentStart??span.start)>=span.start&&(span.contentEnd??span.end)<=(span.end)&&((span.contentEnd??span.end)>(span.contentStart??span.start)));
  const headings=mapping.spans.every(span=>span.headingStart===undefined&&span.headingEnd===undefined||
    span.headingStart!==undefined&&span.headingEnd!==undefined&&span.headingStart>=span.start&&span.headingEnd>span.headingStart&&span.headingEnd<=span.end);
  return{valid:checksumMatches&&fingerprintMatches&&complete&&content&&headings&&mapping.reviewState==="Reviewed",stale:!checksumMatches||!fingerprintMatches,complete,content,headings};
}

export function reviewedPresentationMapping(
  id:string,
  kind:SectionPresentationMapping["kind"],
  sourceFieldKey:string,
  source:string,
  spans:PresentationSourceSpan[],
  reviewedAt:string,
  reviewedBy:string,
):SectionPresentationMapping{
  return{
    id,schemaVersion:1,kind,sourceFieldKey,
    exactSourceChecksum:exactPresentationSourceChecksum(source),
    substantiveSourceFingerprint:substantivePresentationSourceFingerprint(source),
    spans,reviewState:"Reviewed",reviewedAt,reviewedBy,
  };
}

function contiguousSpans(source:string,boundaries:Array<{start:number;end:number;role:PresentationSourceSpan["role"];label?:string;group?:string;headingStart?:number;headingEnd?:number;headingReviewState?:PresentationSourceSpan["headingReviewState"]}>){
  const sorted=[...boundaries].sort((left,right)=>left.start-right.start);
  if(!sorted.length)return[] as PresentationSourceSpan[];
  return sorted.map((item,index)=>({...item,start:index===0?0:item.start,end:index===sorted.length-1?source.length:sorted[index+1].start}));
}

function findInOrder(source:string,values:Array<{text:string;role:PresentationSourceSpan["role"];label?:string;group?:string}>){
  let cursor=0;
  const found:Array<{start:number;end:number;role:PresentationSourceSpan["role"];label?:string;group?:string}>=[];
  for(const value of values){
    const index=source.indexOf(value.text,cursor);
    if(index<0)return[];
    found.push({...value,start:index,end:index+value.text.length});
    cursor=index+value.text.length;
  }
  const spans=contiguousSpans(source,found);
  return spans.map((span,index)=>({...span,contentStart:found[index].start,contentEnd:found[index].end}));
}

function flatten(nodes:ClientPresentationNode[]):ClientPresentationNode[]{
  return nodes.flatMap(node=>[node,...flatten(node.children??[])]);
}

export function paragraphMappingFromPresentation(
  source:string,
  sourceFieldKey:string,
  nodes:ClientPresentationNode[],
  reviewedAt:string,
  reviewedBy:string,
  id:string,
){
  const relevant=flatten(nodes).filter(node=>node.sourceReferences?.some(reference=>reference.key===sourceFieldKey));
  const pieces:Array<{text:string;role:"paragraph"|"list-item";label?:string}>=[];
  for(const node of relevant){
    if(node.type==="unordered-list"||node.type==="ordered-list"){
      pieces.push(...(node.items??[]).map(text=>({text,role:"list-item" as const})));
    }else if(node.text)pieces.push({text:node.text,role:"paragraph",label:node.title??node.label});
  }
  const spans=findInOrder(source,pieces);
  return spans.length?reviewedPresentationMapping(id,"paragraph-boundaries",sourceFieldKey,source,spans,reviewedAt,reviewedBy):undefined;
}

export function labelledMappingFromPresentation(
  source:string,
  sourceFieldKey:string,
  nodes:ClientPresentationNode[],
  reviewedAt:string,
  reviewedBy:string,
  id:string,
){
  let group="";
  const pieces:Array<{text:string;role:"paragraph";label?:string;group?:string}>=[];
  for(const node of nodes){
    if(node.type==="heading"){group=node.text??node.title??group;continue}
    if(!node.sourceReferences?.some(reference=>reference.key===sourceFieldKey)||!node.text)continue;
    pieces.push({text:node.text,role:"paragraph",label:node.title??node.label,group});
  }
  const spans=findInOrder(source,pieces);
  return spans.length?reviewedPresentationMapping(id,"paragraph-boundaries",sourceFieldKey,source,spans,reviewedAt,reviewedBy):undefined;
}

export function semanticListMappingFromLabelled(
  base:SectionPresentationMapping,
  source:string,
  labels:string[],
  id:string,
  reviewedAt:string,
  reviewedBy:string,
){
  const spans:PresentationSourceSpan[]=[];
  for(const span of base.spans){
    if(!span.label||!labels.includes(span.label)){spans.push({...span,role:"paragraph"});continue}
    const contentStart=span.contentStart??span.start,contentEnd=span.contentEnd??span.end;
    const ranges=explicitItemRanges(source.slice(contentStart,contentEnd));
    if(ranges.length<2){spans.push({...span,role:"paragraph"});continue}
    ranges.forEach((range,index)=>spans.push({
      start:index===0?span.start:contentStart+range.start,
      end:index===ranges.length-1?span.end:contentStart+ranges[index+1].start,
      contentStart:contentStart+(range.contentStart??range.start),
      contentEnd:contentStart+(range.contentEnd??range.end),
      role:"list-item",
      label:span.label,
      group:span.group,
    }));
  }
  return reviewedPresentationMapping(id,"semantic-list",base.sourceFieldKey,source,spans,reviewedAt,reviewedBy);
}

function explicitItemRanges(value:string){
  const pattern=/(?:^|\n)\s*(?:[-*•]|\d+[.)])\s+/g;
  const markers=[...value.matchAll(pattern)].map(match=>({start:match.index??0,contentStart:(match.index??0)+match[0].length}));
  if(markers.length>1)return markers.map((marker,index)=>({start:marker.start,end:markers[index+1]?.start??value.length,contentStart:marker.contentStart,contentEnd:markers[index+1]?.start??value.length}));
  const sentences=[...value.matchAll(/[^.!?]+[.!?]+(?:\s+|$)|[^.!?]+$/g)].map(match=>({start:match.index??0,end:(match.index??0)+match[0].length,contentStart:match.index??0,contentEnd:(match.index??0)+match[0].length}));
  return sentences;
}

export function semanticListMapping(
  id:string,
  sourceFieldKey:string,
  source:string,
  reviewedAt:string,
  reviewedBy:string,
){
  const ranges=explicitItemRanges(source);
  if(ranges.length<2)return undefined;
  const spans=contiguousSpans(source,ranges.map(range=>({...range,role:"list-item" as const})));
  return reviewedPresentationMapping(id,"semantic-list",sourceFieldKey,source,spans,reviewedAt,reviewedBy);
}

function actionHeading(source:string,start:number,end:number){
  const text=source.slice(start,end);
  const trimmedStart=start+(text.match(/^\s*(?:[-*•]|\d+[.)])?\s*/)?.[0].length??0);
  const rest=source.slice(trimmedStart,end);
  const explicit=rest.match(/^(.{3,80}?)(?::\s+|[–—]\s+)/);
  if(explicit)return{start:trimmedStart,end:trimmedStart+explicit[1].length};
  const sentence=rest.match(/^((?:\S+\s+){2,6}\S+?)(?=\s+(?:so that|to help|because|which|while|and then)\b)/i);
  return sentence?{start:trimmedStart,end:trimmedStart+sentence[1].length}:undefined;
}

export function numberedActionMapping(
  id:string,
  sourceFieldKey:string,
  source:string,
  reviewedAt:string,
  reviewedBy:string,
){
  const ranges=explicitItemRanges(source);
  if(!ranges.length)return undefined;
  const spans=contiguousSpans(source,ranges.map(range=>{
    const contentStart=range.contentStart??range.start,contentEnd=range.contentEnd??range.end;
    const heading=actionHeading(source,contentStart,contentEnd);
    return{...range,contentStart,contentEnd,role:"action-item" as const,headingStart:heading?.start,headingEnd:heading?.end,headingReviewState:heading?"Reviewed" as const:undefined};
  }));
  return reviewedPresentationMapping(id,"numbered-actions",sourceFieldKey,source,spans,reviewedAt,reviewedBy);
}

export function mappingFor(section:ReportSection,sourceFieldKey:string,kind:SectionPresentationMapping["kind"]){
  const mapping=section.presentationMappings?.find(item=>item.sourceFieldKey===sourceFieldKey&&item.kind===kind);
  const source=mapping?presentationSourceValue(section,sourceFieldKey):undefined;
  return mapping&&source!==undefined&&validatePresentationMapping(mapping,source).valid?{mapping,source}:undefined;
}

export function mappedText(span:PresentationSourceSpan,source:string){
  return source.slice(span.contentStart??span.start,span.contentEnd??span.end).trim().replace(/^(?:[-*•]|\d+[.)])\s+/,"");
}
